/**
 * 회원 배터리 관리 페이지
 *
 * 안드로이드 앱이 날짜 전환 시 전송하는 기기별 일별 클릭/배터리 통계
 * (GET /api/admin/device-daily-stats)를 기기 단위로 묶어서 보여준다.
 *
 * - 기기 목록: 기기별 최신(가장 최근 날짜) 스냅샷 1행만 계산해서 보여준다.
 *   (기기당 전체 히스토리는 여기서 들고 있지 않음 — 아래 참고)
 * - 검색: 기기 주소 / 연락처
 * - 같은 링을 다른 전화번호에서 보낸 경우에도 기록은 분리 보존하고, 목록에는 수신 전화번호 수를 표시
 * - 버전(V1/V2) 필터: BaeringUser.deviceVersion을 deviceAddress로 매칭해서 사용
 *   (일별 통계 자체엔 버전 정보가 없어서 회원 목록과 조인해야 함)
 * - 행 클릭 → MemberBatteryDetail(/admin/member/battery/:address)로 이동
 * - 정렬·페이지네이션: 회원 목록과 동일하게 URL 쿼리 파라미터로 유지
 *   ?q=검색어&ver=V2&sort=latestBatteryV&dir=asc&page=2
 *   (기본 정렬은 최근 기록일 내림차순이라 URL에 남기지 않음)
 *
 * [인라인 펼치기 → 상세 페이지 전환 이유]
 *   처음엔 행을 클릭하면 그 자리에서 펼치는 방식이었는데, 기기가 수천 대 단위로
 *   늘어나면 이 목록 자체가 "전체 기기 × 전체 날짜" 데이터를 다 들고 있어야 해서
 *   무거워진다(추후 최적화 필요). 최소한 "펼친 히스토리"를 목록에서 미리 들고
 *   있지 않도록 상세 페이지로 분리하고, 상세 페이지는 그 기기 하나의 기록만
 *   GET /api/admin/device-daily-stats/device/{address} 로 그때그때 조회하게 했다.
 *
 * "회원 관리" 하위 기능이라 사이드바에 별도 메뉴를 추가하지 않고,
 * 회원 목록 페이지와 동일한 상단 탭(회원 목록 / 배터리 관리)으로 오간다.
 */
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { dailyStatsService } from "../../services/dailyStatsService";
import { userService } from "../../services/userService";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ErrorAlert from "../../components/ui/ErrorAlert";
import StatsCard from "../../components/ui/StatsCard";
import { formatDate } from "../../utils/format";
import MemberSubNav from "../../components/admin/MemberSubNav";

type VersionFilter = "all" | "V1" | "V2";
type SortField = "deviceAddress" | "phoneNumberCount" | "latestBatteryV" | "latestClickCount" | "latestDate" | "recordCount";
type SortDirection = "asc" | "desc";

const DEFAULT_SORT_FIELD: SortField = "latestDate";
const DEFAULT_SORT_DIRECTION: SortDirection = "desc";
const ITEMS_PER_PAGE = 50;

const COLUMNS: { label: string; field: SortField | null }[] = [
  { label: "기기",          field: "deviceAddress" },
  { label: "수집 전화번호", field: "phoneNumberCount" },
  { label: "버전",          field: null },
  { label: "최근 배터리",   field: "latestBatteryV" },
  { label: "최근 클릭수",   field: "latestClickCount" },
  { label: "최근 기록일",   field: "latestDate" },
  { label: "기록 일수",     field: "recordCount" },
];

/** 기기 단위로 묶은 요약 정보 — 목록 한 행 단위. 전체 히스토리는 상세 페이지에서 따로 조회한다. */
interface DeviceSummary {
  deviceAddress: string;
  phoneNumber: string | null;
  collectedPhoneNumbers: string[];
  deviceVersion: string;      // BaeringUser와 매칭 안 되면 "-"
  latestDate: string;
  latestClickCount: number;
  latestBatteryV: number;
  recordCount: number;
  phoneNumberCount: number;
}

const VERSION_TABS = [
  { key: "all", label: "전체" },
  { key: "V1",  label: "V1" },
  { key: "V2",  label: "V2" },
] as const;

export default function MemberBatteryList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchTerm    = searchParams.get("q")   ?? "";
  const versionFilter = (searchParams.get("ver") ?? "all") as VersionFilter;
  const sortField     = (searchParams.get("sort") ?? DEFAULT_SORT_FIELD) as SortField;
  const sortDirection = (searchParams.get("dir")  ?? DEFAULT_SORT_DIRECTION) as SortDirection;
  const currentPage   = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10));

  // ─── URL 파라미터 업데이트 헬퍼 (기본값은 URL에서 지움) ──────────────────
  const updateParams = useCallback(
    (updates: Record<string, string | null | undefined>, resetPage = false) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(updates)) {
            if (
              v == null || v === "" || v === "all" ||
              (k === "sort" && v === DEFAULT_SORT_FIELD) ||
              (k === "dir" && v === DEFAULT_SORT_DIRECTION)
            ) {
              next.delete(k);
            } else {
              next.set(k, v);
            }
          }
          if (resetPage) next.delete("page");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setSearchTerm    = (v: string)        => updateParams({ q: v }, true);
  const setVersionFilter = (v: VersionFilter) => updateParams({ ver: v }, true);
  const setCurrentPage   = (page: number)     => updateParams({ page: page === 1 ? null : String(page) });

  const handleSort = useCallback(
    (field: SortField) => {
      if (sortField === field) {
        updateParams({ dir: sortDirection === "asc" ? "desc" : "asc" }, true);
      } else {
        updateParams({ sort: field, dir: "asc" }, true);
      }
    },
    [sortField, sortDirection, updateParams]
  );

  // ─── 데이터 쿼리 ────────────────────────────────────────────────────────
  // NOTE: 지금은 전체 일별 통계를 한 번에 받아서 클라이언트에서 기기별로 묶는다.
  // 기기·기간이 커지면 "기기별 최신 스냅샷만" 반환하는 백엔드 엔드포인트를 새로 만들어
  // 이 getAll() 호출을 대체하는 게 좋다 (상세 페이지는 이미 기기별 조회로 분리해둠).
  const { data: stats, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ["dailyStats"],
    queryFn: dailyStatsService.getAll,
  });

  const { data: users, isLoading: usersLoading, error: usersError } = useQuery({
    queryKey: ["users"],
    queryFn: userService.getAllUsers,
  });

  // deviceAddress → deviceVersion 매핑 (일별 통계엔 버전 정보가 없어서 회원 목록과 조인)
  const versionByDevice = useMemo(() => {
    const map = new Map<string, string>();
    users?.forEach((u) => map.set(u.deviceAddress, u.deviceVersion));
    return map;
  }, [users]);

  // ─── 기기 단위로 그룹핑 (최신 스냅샷만 유지, 히스토리는 들고 있지 않음) ─────
  const deviceSummaries = useMemo<DeviceSummary[]>(() => {
    if (!stats) return [];
    const latestByDevice = new Map<string, {
      count: number;
      phoneNumbers: Set<string>;
      latest: (typeof stats)[number];
    }>();
    stats.forEach((s) => {
      const entry = latestByDevice.get(s.deviceAddress);
      if (!entry) {
        latestByDevice.set(s.deviceAddress, {
          count: 1,
          phoneNumbers: new Set(s.phoneNumber ? [s.phoneNumber] : []),
          latest: s,
        });
      } else {
        entry.count += 1;
        if (s.phoneNumber) entry.phoneNumbers.add(s.phoneNumber);
        if (
          s.statDate > entry.latest.statDate ||
          (s.statDate === entry.latest.statDate && (s.updatedAt ?? "") > (entry.latest.updatedAt ?? ""))
        ) entry.latest = s;
      }
    });

    return Array.from(latestByDevice.entries())
      .map(([deviceAddress, { count, phoneNumbers, latest }]) => ({
        deviceAddress,
        phoneNumber: latest.phoneNumber,
        collectedPhoneNumbers: Array.from(phoneNumbers).sort(),
        deviceVersion: versionByDevice.get(deviceAddress) ?? "-",
        latestDate: latest.statDate,
        latestClickCount: latest.clickCount,
        latestBatteryV: latest.lastBatteryV,
        recordCount: count,
        phoneNumberCount: phoneNumbers.size,
      }))
      .sort((a, b) => b.latestDate.localeCompare(a.latestDate));
  }, [stats, versionByDevice]);

  const summaryStats = useMemo(() => {
    const all = deviceSummaries.length;
    const V1  = deviceSummaries.filter((d) => d.deviceVersion === "V1").length;
    const V2  = deviceSummaries.filter((d) => d.deviceVersion === "V2").length;
    const avgBattery = all > 0
      ? deviceSummaries.reduce((sum, d) => sum + d.latestBatteryV, 0) / all
      : 0;
    return { all, V1, V2, avgBattery };
  }, [deviceSummaries]);

  const filteredDevices = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const filtered = deviceSummaries.filter((d) => {
      const matchesSearch =
        d.deviceAddress.toLowerCase().includes(term) ||
        (d.phoneNumber ?? "").includes(searchTerm);
      const matchesVersion = versionFilter === "all" || d.deviceVersion === versionFilter;
      return matchesSearch && matchesVersion;
    });

    // 값이 같으면 기존 순서(최근 기록일 내림차순)를 유지한다 (Array.sort는 안정 정렬).
    filtered.sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;
      switch (sortField) {
        case "deviceAddress":    aValue = a.deviceAddress;    bValue = b.deviceAddress;    break;
        case "phoneNumberCount": aValue = a.phoneNumberCount; bValue = b.phoneNumberCount; break;
        case "latestBatteryV":   aValue = a.latestBatteryV;   bValue = b.latestBatteryV;   break;
        case "latestClickCount": aValue = a.latestClickCount; bValue = b.latestClickCount; break;
        case "latestDate":       aValue = a.latestDate;       bValue = b.latestDate;       break;
        case "recordCount":      aValue = a.recordCount;      bValue = b.recordCount;      break;
        default:                 return 0;
      }
      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return filtered;
  }, [deviceSummaries, searchTerm, versionFilter, sortField, sortDirection]);

  const totalPages = Math.ceil(filteredDevices.length / ITEMS_PER_PAGE);
  const paginatedDevices = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredDevices.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredDevices, currentPage]);

  const sortIcon = (field: SortField) =>
    sortField === field
      ? sortDirection === "asc" ? "fa-arrow-up text-primary" : "fa-arrow-down text-primary"
      : "fa-sort text-gray-400";

  if (statsLoading || usersLoading) return <LoadingSpinner />;
  if (statsError || usersError) return <ErrorAlert />;

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">회원 배터리 관리</h1>
        <p className="text-gray-500 mt-2">
          기기별 마지막 배터리 전압과 일별 클릭 통계를 조회합니다
        </p>
      </div>

      <MemberSubNav active="battery" />

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatsCard
          label="전체 기기"
          value={summaryStats.all}
          icon="fas fa-mobile-alt"
          borderColor="border-primary"
          iconColor="text-primary"
          bgColor="bg-primary/10"
        />
        <StatsCard
          label="V1 기기"
          value={summaryStats.V1}
          icon="fas fa-mobile-alt"
          borderColor="border-blue-500"
          iconColor="text-blue-600"
          bgColor="bg-blue-500/10"
          valueColor="text-blue-600"
        />
        <StatsCard
          label="V2 기기"
          value={summaryStats.V2}
          icon="fas fa-mobile-alt"
          borderColor="border-purple-500"
          iconColor="text-purple-600"
          bgColor="bg-purple-500/10"
          valueColor="text-purple-600"
        />
        <StatsCard
          label="평균 배터리"
          value={`${summaryStats.avgBattery.toFixed(2)}V`}
          icon="fas fa-battery-three-quarters"
          borderColor="border-marker-orange"
          iconColor="text-marker-orange"
          bgColor="bg-marker-orange/10"
          valueColor="text-marker-orange"
        />
      </div>

      {/* Search + Version Filter */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-center">
          <div className="relative">
            <i className="fas fa-search absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              placeholder="기기 주소 또는 연락처로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div className="flex bg-gray-50 border border-gray-200 rounded-lg p-1">
            {VERSION_TABS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setVersionFilter(key)}
                className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${
                  versionFilter === key
                    ? "bg-gradient-to-r from-primary to-[#5FA060] text-white shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-4 text-sm text-gray-500">
          총 <span className="font-semibold text-primary">{filteredDevices.length}</span>대의 기기
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-150">
            <thead className="bg-gradient-to-r from-primary-lighter to-bg-light">
              <tr>
                {COLUMNS.map(({ label, field }) => (
                  <th
                    key={label}
                    className={`px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap ${
                      field ? "cursor-pointer hover:bg-primary-lighter/50 transition-colors" : ""
                    }`}
                    onClick={field ? () => handleSort(field) : undefined}
                  >
                    <div className="flex items-center justify-center space-x-1">
                      <span>{label}</span>
                      {field && <i className={`fas ${sortIcon(field)}`}></i>}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-150">
              {paginatedDevices.length > 0 ? (
                paginatedDevices.map((d) => (
                  <tr
                    key={d.deviceAddress}
                    onClick={() => navigate(`/admin/member/battery/${encodeURIComponent(d.deviceAddress)}`)}
                    className="hover:bg-primary-lighter transition-colors cursor-pointer group"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-3">
                        <div className="w-9 h-9 bg-gradient-to-br from-primary to-[#5FA060] rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">
                          <i className="fas fa-mobile-alt"></i>
                        </div>
                        <div className="text-left">
                          <code className="text-xs bg-gray-50 px-2 py-1 rounded text-gray-600 block w-fit">{d.deviceAddress}</code>
                        </div>
                        <i className="fas fa-chevron-right text-gray-300 text-xs group-hover:translate-x-0.5 transition-transform"></i>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {d.phoneNumberCount > 0 ? (
                        <div>
                          <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                            <i className="fas fa-phone mr-1"></i>{d.phoneNumberCount}개
                          </span>
                          <p className="mt-1 max-w-40 truncate text-xs text-gray-400" title={d.collectedPhoneNumbers.join(", ")}>
                            {d.collectedPhoneNumbers.join(", ")}
                          </p>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">미수집</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`px-3 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full ${
                        d.deviceVersion === "V1" ? "bg-blue-50 text-blue-600 border border-blue-200"
                          : d.deviceVersion === "V2" ? "bg-purple-50 text-purple-600 border border-purple-200"
                          : "bg-gray-50 text-gray-600 border border-gray-200"
                      }`}>
                        <i className="fas fa-mobile-alt mr-1"></i>{d.deviceVersion}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="px-3 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-marker-orange/10 text-marker-orange border border-marker-orange/30">
                        <i className="fas fa-battery-three-quarters mr-1"></i>{d.latestBatteryV.toFixed(2)}V
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className="px-3 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-primary/10 text-primary border border-primary/30">
                        <i className="fas fa-hand-pointer mr-1"></i>{d.latestClickCount.toLocaleString()}회
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
                      <i className="fas fa-calendar-alt mr-2"></i>{formatDate(d.latestDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
                      {d.recordCount}일
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    <i className="fas fa-search text-4xl mb-4 text-gray-300 block"></i>
                    <p>검색 결과가 없습니다</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-150 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <span className="font-medium">{filteredDevices.length}</span>개 중{" "}
                <span className="font-medium">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span>-
                <span className="font-medium">{Math.min(currentPage * ITEMS_PER_PAGE, filteredDevices.length)}</span>개 표시
              </div>
              <div className="flex items-center space-x-2">
                {([
                  { label: <i className="fas fa-angle-double-left"></i>, page: 1,               disabled: currentPage === 1 },
                  { label: <i className="fas fa-angle-left"></i>,        page: currentPage - 1, disabled: currentPage === 1 },
                ] as const).map(({ label, page, disabled }, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(page)}
                    disabled={disabled}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      disabled ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-white text-gray-700 hover:bg-primary hover:text-white shadow-sm"
                    }`}
                  >
                    {label}
                  </button>
                ))}

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5)                    pageNum = i + 1;
                  else if (currentPage <= 3)              pageNum = i + 1;
                  else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else                                    pageNum = currentPage - 2 + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        currentPage === pageNum
                          ? "bg-gradient-to-r from-primary to-[#5FA060] text-white shadow-md"
                          : "bg-white text-gray-700 hover:bg-gray-100 shadow-sm"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                {([
                  { label: <i className="fas fa-angle-right"></i>,        page: currentPage + 1, disabled: currentPage === totalPages },
                  { label: <i className="fas fa-angle-double-right"></i>, page: totalPages,      disabled: currentPage === totalPages },
                ] as const).map(({ label, page, disabled }, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(page)}
                    disabled={disabled}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      disabled ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-white text-gray-700 hover:bg-primary hover:text-white shadow-sm"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
